import { useState, useRef, useEffect } from 'react'
import type { DocumentWithAttributes } from 'lib/models/document'
import styled from 'styled-components'
import { Wrapper, H, ChevronRightIcon, ChevronDownIcon } from '@farewill/ui'
import { COLOR } from '@farewill/ui/tokens'
import { DocumentCardSaved } from './document-card-saved'
import { DOCUMENT_CATEGORIES } from 'utils/enums'

const StyledWrapper = styled(Wrapper)`
  display: flex;
  align-items: center;
`

const AnimatedListWrapper = styled(Wrapper)<{
  $expanded: boolean
  $contentHeight: number
}>`
  overflow: hidden;
  max-height: ${({ $expanded, $contentHeight }) =>
    $expanded ? `${$contentHeight}px` : '0'};
  transition: max-height 0.15s ease-in-out;
`

export const SavedDocumentsExpandableCategory = ({
  category,
  documents,
}: {
  category: (typeof DOCUMENT_CATEGORIES)[number]
  documents: DocumentWithAttributes[]
}) => {
  const [isExpanded, setIsExpanded] = useState(documents.length <= 5)
  const [contentHeight, setContentHeight] = useState(0)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isExpanded && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight)
    }
  }, [isExpanded, documents])

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded)
  }

  if (documents.length === 0) return null

  return (
    <Wrapper tag="li" key={category.value} margin={['M', 0]}>
      <StyledWrapper
        margin={['L', 0, 'S']}
        onClick={handleExpandClick}
        style={{ cursor: 'pointer' }}
      >
        <H size="S" color={COLOR.GREY.DARK} margin={[0, 'S', 0, 0]}>
          {category.label} ({documents.length})
        </H>
        {isExpanded ? (
          <ChevronDownIcon size="S" color={COLOR.GREY.DARK} />
        ) : (
          <ChevronRightIcon size="S" color={COLOR.GREY.DARK} />
        )}
      </StyledWrapper>
      <AnimatedListWrapper
        tag="div"
        $expanded={isExpanded}
        $contentHeight={contentHeight}
      >
        <div ref={contentRef}>
          <Wrapper tag="ul">
            {documents.map((document) => (
              <DocumentCardSaved key={document.id} document={document} />
            ))}
          </Wrapper>
        </div>
      </AnimatedListWrapper>
    </Wrapper>
  )
}
